import React, { useMemo, useState } from "react";

import { Grid, Skeleton } from "@mui/material";
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
} from "victory";

import {
  ConsumerMonitoringData,
  ConsumerMonitoringUnits,
} from "libLegacy/api/models";

import {
  AXIS_STYLE,
  CHART_PADDING,
  COLOR_SCHEME,
  useChartStyles,
} from "./chartStyles";

import {
  formatDayMonthLabel,
  formatHourLabel,
  formatKwhLabel,
  normalizeTimeValues,
  formatMoneyLabel,
  formatCO2eLabel,
} from "logic/Charts";
import { SelectorGranularitiesProps } from "components/admin/SelectorGranularities";
import { ChartCaptionToggle } from "./ChartCaptionToggle";

export type ChartConsumerMonitoringProps = {
  data: ConsumerMonitoringData;
  unit: ConsumerMonitoringUnits;
  granularity: SelectorGranularitiesProps["granularity"];
  isLoading?: boolean;
};

const formatUnitLabel = (value, unit) => {
  switch (unit) {
    case "kWh":
      return formatKwhLabel(value);
    case "money":
      return formatMoneyLabel(value);
    case "CO2e":
      return formatCO2eLabel(value);
  }
};

const formatTimeGranularity = (value, granularity) => {
  switch (granularity) {
    case "day":
      return formatHourLabel(value);
    default:
      return formatDayMonthLabel(value);
  }
};

export const ChartConsumerMonitoring = ({
  data,
  unit,
  granularity,
  isLoading,
}: ChartConsumerMonitoringProps) => {
  const { classes } = useChartStyles();
  const [isMainVisible, setIsMainVisible] = useState<boolean>(true);
  const [invisibleSets, setInvisibleSets] = useState<Record<string, boolean>>(
    {}
  );

  const [mainTotal, secondaryData] = useMemo(() => {
    let main;

    const secondary = data.flatMap(({ name, data }) => {
      if (name.toLocaleLowerCase() === "geral") {
        main = data;
        return [];
      } else {
        return [{ data, name }];
      }
    });

    return [main, secondary];
  }, [data]);

  const toggleVisibility = (name: string) => {
    let newInvisibleSets = { ...invisibleSets };

    if (!invisibleSets[name]) {
      // make it invisible
      newInvisibleSets[name] = true;
    } else {
      // make it visible
      delete newInvisibleSets[name];
    }

    setInvisibleSets(newInvisibleSets);
  };

  const Chart = () => (
    <Grid item container direction="column" className={classes.bordered}>
      <Grid item container>
        <svg width="0" height="0" viewBox="0 0 0 0">
          <defs>
            <linearGradient
              id="mainGradient"
              x1="217.222"
              y1="-0.919678"
              x2="217.222"
              y2="189.116"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#E3BB5C" />
              <stop offset="1" stopColor="#E0FFE1" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
        <VictoryChart padding={CHART_PADDING} domainPadding={6}>
          {/* @ts-ignore */}
          <VictoryAxis
            style={AXIS_STYLE}
            tickLabelComponent={<VictoryLabel angle={-45} dx={-8} dy={-5} />}
            tickFormat={(point) => formatTimeGranularity(point, granularity)}
          />

          <VictoryAxis
            dependentAxis
            style={AXIS_STYLE}
            tickFormat={(point) => formatUnitLabel(point, unit)}
          />

          {mainTotal && isMainVisible && (
            <VictoryArea
              data={mainTotal}
              x={normalizeTimeValues}
              y={unit}
              sortKey="x"
              style={{
                data: {
                  stroke: "#ED6C02",
                  fill: "url(#mainGradient)",
                  strokeWidth: "0.752px",
                },
              }}
            />
          )}

          {secondaryData
            .filter(({ name }) => !invisibleSets[name])
            .map(({ name, data }, i) => (
              <VictoryLine
                key={name}
                data={data}
                sortKey="x"
                y={unit}
                x={normalizeTimeValues}
                style={{
                  data: {
                    stroke: COLOR_SCHEME[i] || "black",
                    strokeWidth: "0.752px",
                  },
                }}
              />
            ))}
        </VictoryChart>
      </Grid>

      <Grid container item gap={1} justifyContent="center">
        {!!mainTotal && (
          <Grid item container justifyContent="center" sx={{ mr: 2, flex: 1 }}>
            <ChartCaptionToggle
              label="Geral"
              color="#ED6C02"
              isChecked={isMainVisible}
              onToggle={() => {
                setIsMainVisible((prev) => !prev);
              }}
            />
          </Grid>
        )}
        {secondaryData?.map(({ name }, i) => (
          <Grid item container justifyContent="center" sx={{ mr: 2, flex: 1 }}>
            <ChartCaptionToggle
              isChecked={!invisibleSets[name]}
              color={COLOR_SCHEME[i]}
              label={name}
              onToggle={() => {
                toggleVisibility(name);
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );

  if (isLoading) {
    return (
      <Skeleton variant="rectangular">
        <Chart />
      </Skeleton>
    );
  } else return <Chart />;
};
